import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiAlertCircle, FiLoader, FiSearch, FiGlobe, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/website-crawler';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

function truncateUrl(url, max) {
  if (url.length <= max) return url;
  return url.slice(0, max - 1) + '…';
}

export default function WebsiteCrawlerPage() {
  const inputId = useId();
  const helperId = `${inputId}-helper`;
  const statusId = `${inputId}-status`;
  const resultsRef = useRef(null);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState('url');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    document.title = 'Website crawler — see pages crawled for your site | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) desc.setAttribute('content', 'See which of your website pages have been crawled and indexed or stored for indexing. Free website crawler from WebCodeCraft.');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const pageTitle = 'Website crawler — see pages crawled for your site | WebCodeCraft';
    const pageDesc = 'See which of your website pages have been crawled and indexed or stored for indexing. Free website crawler from WebCodeCraft.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);
    if (twTitle) twTitle.setAttribute('content', pageTitle);
    if (twDesc) twDesc.setAttribute('content', pageDesc);
    if (twUrl) twUrl.setAttribute('content', CANONICAL);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebPage',
      name: pageTitle, url: CANONICAL, description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'website-crawler');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setData(null);
    const d = input.trim();
    if (!d) { setError('Enter your website address.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/website-crawler', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: d })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof json.error === 'string' ? json.error : 'Something went wrong.'); return; }
      setData(json);
      requestAnimationFrame(() => { resultsRef.current?.focus(); });
    } catch { setError('Network error. Try again in a moment.'); }
    finally { setLoading(false); }
  }, [input]);

  const toggleSort = useCallback((key) => {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(key === 'url'); }
  }, [sortKey]);

  const sortedPages = useMemo(() => {
    const pages = data?.pages || [];
    return pages.slice().sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [data, sortKey, sortAsc]);

  const siteUrl = data?.domain ? `https://${data.domain}` : '';

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return sortAsc ? <FiChevronUp size={13} className="inline ml-0.5" /> : <FiChevronDown size={13} className="inline ml-0.5" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="wc-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/check-your-seo" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">Website crawler</span>
            </p>
            <h1 id="wc-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Website crawler
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6" id={helperId}>
              This will show which of your website pages have been crawled and indexed or stored for indexing. Enter your domain below, then run the crawler to list each page URL and its content type.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor={inputId} className="block text-sm font-semibold text-slate-800 mb-1.5">Your website</label>
                <input id={inputId} type="text" name="domain" autoComplete="url"
                  placeholder="e.g. example.com" value={input}
                  onChange={(ev) => setInput(ev.target.value)}
                  className="form-input w-full max-w-md" aria-describedby={helperId}
                  disabled={loading} maxLength={253} />
              </div>
              <m.button type="submit" className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                disabled={loading} whileTap={{ scale: 0.98 }}>
                {loading
                  ? <><FiLoader className="animate-spin" aria-hidden="true" />Running crawler…</>
                  : <><FiSearch aria-hidden="true" />Run website crawler</>}
              </m.button>
            </form>
            <div id={statusId} role="status" aria-live="polite" className="mt-6 min-h-[1.25rem] text-sm">
              {error && (
                <p className="text-red-700 font-medium flex items-start gap-2">
                  <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}
              {loading && <p className="text-slate-500">This can take up to a minute for large sites.</p>}
            </div>
          </m.div>
        </section>

        {data?.ok && (
          <section ref={resultsRef} tabIndex={-1}
            className="container mx-auto px-4 pb-16 max-w-5xl outline-none space-y-8"
            aria-label="Crawl results">

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                <FiGlobe className="text-indigo-600" size={24} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-500 mb-1">Website you entered</p>
                <a href={siteUrl} target="_blank" rel="noopener noreferrer"
                  className="text-lg sm:text-xl font-semibold text-indigo-700 hover:underline underline-offset-2 break-all inline-flex items-center gap-2">
                  {siteUrl}
                  <FiExternalLink className="shrink-0 text-indigo-400" size={18} />
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-900">
                  Pages found ({sortedPages.length})
                </h2>
                <p className="text-sm text-slate-500 mt-1">Each row is a page on your site that appeared in the crawl, with its content type.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left py-2.5 px-4 font-medium cursor-pointer select-none" onClick={() => toggleSort('url')}>
                        URL <SortIcon col="url" />
                      </th>
                      <th className="text-left py-2.5 px-4 font-medium cursor-pointer select-none w-48 sm:w-56" onClick={() => toggleSort('mime')}>
                        Content type <SortIcon col="mime" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedPages.length === 0 && (
                      <tr><td colSpan={2} className="py-8 text-center text-slate-500">No pages returned for this search.</td></tr>
                    )}
                    {sortedPages.slice(0, 200).map((p, i) => (
                      <tr key={`${p.url}-${i}`} className="hover:bg-slate-50/60 transition-colors">
                        <td className="py-2.5 px-4 max-w-[20rem] sm:max-w-none">
                          <a href={p.url} target="_blank" rel="noopener noreferrer"
                            className="text-indigo-700 hover:underline underline-offset-2 inline-flex items-center gap-1 break-all leading-snug"
                            title={p.url}>
                            {truncateUrl(p.url, 90)}
                            <FiExternalLink className="shrink-0 text-indigo-400" size={12} />
                          </a>
                        </td>
                        <td className="py-2.5 px-4 text-slate-700 text-xs sm:text-sm">{p.mime || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sortedPages.length > 200 && (
                <p className="text-center text-xs text-slate-400 py-3 border-t border-slate-100">Showing first 200 of {sortedPages.length} pages</p>
              )}
            </div>

            <div className="border-t border-slate-200 pt-6 text-center">
              <p className="text-sm text-slate-500 mb-4">
                Want to improve your site or fix technical issues? Get in touch.
              </p>
              <a href={`${SITE_ORIGIN}/#contact`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm">
                Contact WebCodeCraft
              </a>
            </div>
          </section>
        )}
        <Footer />
      </main>
    </div>
  );
}
